import React, { useState, useEffect } from 'react';

const Reviews = () => {
  const [vouches, setVouches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [displayedVouches, setDisplayedVouches] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState(null);
  const vouchesPerPage = 30;

  useEffect(() => {
    const fetchVouches = async () => {
      try {
        console.log('Fetching vouches...');
        const response = await fetch('https://myvouch.es/api/vouches/olymp');
        console.log('Response status:', response.status);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        console.log('Received data:', data);
        
        if (!Array.isArray(data)) {
          throw new Error('Data is not an array');
        }

        // Trier les avis par date décroissante (plus récent en premier)
        const sortedVouches = data.sort((a, b) => new Date(b.date) - new Date(a.date));
        console.log('Sorted vouches:', sortedVouches.length);
        
        setVouches(sortedVouches);
        setDisplayedVouches(sortedVouches.slice(0, vouchesPerPage));
        setError(null);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching vouches:', error);
        setError('Failed to load reviews. Please try again later.');
        setLoading(false);
      }
    };

    fetchVouches();
    // Rafraîchir les avis toutes les 30 secondes
    const interval = setInterval(fetchVouches, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadMoreVouches = () => {
    const nextPage = currentPage + 1;
    const endIndex = nextPage * vouchesPerPage;
    setDisplayedVouches(vouches.slice(0, endIndex));
    setCurrentPage(nextPage);
  };

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#22c55e]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="neon-button px-6 py-2"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!vouches.length) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 mb-4">No reviews available at the moment.</p>
        </div>
      </div>
    );
  }

  // Calculer la note moyenne
  const averageRating = (vouches.reduce((acc, vouch) => acc + parseInt(vouch.stars || 0, 10), 0) / vouches.length).toFixed(1);
  const satisfactionRate = ((vouches.filter(vouch => parseInt(vouch.stars || 0, 10) >= 4).length / vouches.length) * 100).toFixed(0);

  return (
    <div className="min-h-screen py-16">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Customer <span className="text-[#22c55e]">Reviews</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
            Real-time feedback from our valued customers
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-400">
            <div className="w-2 h-2 bg-[#22c55e] rounded-full animate-pulse"></div>
            Live synchronization with our Discord server
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
          <div className="glass-card p-8 text-center relative group overflow-hidden transition-all duration-500 hover:scale-105">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-[#22c55e]/5 blur-xl group-hover:bg-[#22c55e]/10 transition-all duration-500"></div>
            {/* Animated corner effects */}
            <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-[#22c55e]/20 to-transparent transform -translate-x-full -translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-tl from-[#22c55e]/20 to-transparent transform translate-x-full translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            
            <div className="relative">
              <div className="text-[#22c55e] text-5xl font-bold mb-2 transform transition-all duration-500 group-hover:scale-110 group-hover:text-[#22c55e] flex justify-center items-baseline">
                {vouches.length}
                <span className="text-3xl ml-1">+</span>
              </div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-500">Total Reviews</div>
            </div>
          </div>

          <div className="glass-card p-8 text-center relative group overflow-hidden transition-all duration-500 hover:scale-105">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-[#22c55e]/5 blur-xl group-hover:bg-[#22c55e]/10 transition-all duration-500"></div>
            {/* Animated corner effects */}
            <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-[#22c55e]/20 to-transparent transform -translate-x-full -translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-tl from-[#22c55e]/20 to-transparent transform translate-x-full translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            
            <div className="relative">
              <div className="text-[#22c55e] text-5xl font-bold mb-2 transform transition-all duration-500 group-hover:scale-110 group-hover:text-[#22c55e] flex justify-center">
                {averageRating}
                <div className="flex ml-2 mt-2">
                  <svg className="w-6 h-6 text-[#22c55e]" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </div>
              </div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-500">Average Rating</div>
            </div>
          </div>

          <div className="glass-card p-8 text-center relative group overflow-hidden transition-all duration-500 hover:scale-105">
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-[#22c55e]/5 blur-xl group-hover:bg-[#22c55e]/10 transition-all duration-500"></div>
            {/* Animated corner effects */}
            <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-br from-[#22c55e]/20 to-transparent transform -translate-x-full -translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            <div className="absolute bottom-0 right-0 w-16 h-16 bg-gradient-to-tl from-[#22c55e]/20 to-transparent transform translate-x-full translate-y-full group-hover:translate-x-0 group-hover:translate-y-0 transition-transform duration-700"></div>
            
            <div className="relative">
              <div className="text-[#22c55e] text-5xl font-bold mb-2 transform transition-all duration-500 group-hover:scale-110 group-hover:text-[#22c55e]">
                {satisfactionRate}%
              </div>
              <div className="text-gray-300 group-hover:text-white transition-colors duration-500">Satisfaction Rate</div>
            </div>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedVouches.map((vouch, index) => (
            <div key={index} className="glass-card p-6 relative group hover:border-[#22c55e]/30 transition-all duration-300">
              <div className="flex items-start gap-4">
                <img
                  src={vouch.discord_avatar?.startsWith('https://') ? vouch.discord_avatar : 'https://myvouch.es/storage/avatars-vouches/default.png'}
                  alt={vouch.discord_name}
                  className="w-10 h-10 rounded-full"
                  onError={(e) => {
                    e.target.src = 'https://myvouch.es/storage/avatars-vouches/default.png';
                  }}
                />
                <div className="flex-1">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-[#22c55e] truncate">
                        @{vouch.discord_name}
                      </h3>
                      <a 
                        href={`https://discordlookup.com/user/${vouch.discord_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-gray-400 hover:text-[#22c55e] transition-colors duration-200"
                      >
                        ({vouch.discord_id})
                      </a>
                    </div>
                    <div className="flex">
                      {[...Array(parseInt(vouch.stars || 0, 10))].map((_, i) => (
                        <svg key={i} className="w-4 h-4 text-[#22c55e]" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                  </div>
                  <p className="mt-2 text-gray-300">{vouch.content || vouch.message}</p>
                  <div className="mt-2 text-xs text-gray-400">
                    {formatDate(vouch.date)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {displayedVouches.length < vouches.length && (
          <div className="flex justify-center mt-8">
            <button
              onClick={loadMoreVouches}
              className="neon-button px-8 py-3"
            >
              Load More Reviews
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews; 